(() => {
  'use strict';

  /*
    Product Set Builder layout presets — v2.15 fit-scale restore.

    Product Set Builder layout presets.

    Edit only this map when the number of columns/rows or stage proportions change.
    stageAspect is [width, height]. The 3-print preset intentionally keeps the
    original one-row 4:3 stage and legacy horizontal spacing calculation.
  */
  const PRESETS = Object.freeze({
    1: Object.freeze({ count: 1, columns: 1, rows: 1, stageAspect: [4, 3], preferredSlotWidthPct: 36, label: '1' }),
    2: Object.freeze({ count: 2, columns: 2, rows: 1, stageAspect: [4, 3], preferredSlotWidthPct: 30, label: '2' }),
    3: Object.freeze({ count: 3, columns: 3, rows: 1, stageAspect: [4, 3], label: '3', preserveLegacyThree: true }),
    4: Object.freeze({ count: 4, columns: 2, rows: 2, stageAspect: [8, 7], safeTopPx: 44, safeBottomPx: 26, label: '4' }),
    6: Object.freeze({ count: 6, columns: 3, rows: 2, stageAspect: [8, 7], safeTopPx: 44, safeBottomPx: 26, label: '6' }),
    8: Object.freeze({ count: 8, columns: 4, rows: 2, stageAspect: [8, 7], safeTopPx: 44, safeBottomPx: 26, label: '8' }),
    9: Object.freeze({ count: 9, columns: 3, rows: 3, stageAspect: [1, 1], safeTopPx: 46, safeBottomPx: 28, label: '9' }),
    12: Object.freeze({ count: 12, columns: 4, rows: 3, stageAspect: [1, 1], safeTopPx: 46, safeBottomPx: 28, label: '12' })
  });

  const SLOT_COLORS = Object.freeze([
    '#2f80ed', '#f2994a', '#27ae60', '#9b51e0',
    '#eb5757', '#2d9cdb', '#f2c94c', '#219653',
    '#bb6bd9', '#56ccf2', '#6fcf97', '#b7791f'
  ]);

  const SUPPORTED_COUNTS = Object.freeze(
    Object.keys(PRESETS).map(Number).sort((a, b) => a - b)
  );

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function getPreset(count) {
    const safeCount = Number(count);
    return PRESETS[safeCount] || PRESETS[3];
  }

  function isSupportedCount(count) {
    return Object.prototype.hasOwnProperty.call(PRESETS, Number(count));
  }

  function getStageAspectCss(count) {
    const preset = getPreset(count);
    return `${preset.stageAspect[0]} / ${preset.stageAspect[1]}`;
  }

  function getSlotColor(index) {
    const safeIndex = Math.max(0, Number(index) || 0);
    return SLOT_COLORS[safeIndex % SLOT_COLORS.length];
  }

  function calculateLayout(params = {}) {
    const stageWidth = Number(params.stageWidth) || 0;
    const stageHeight = Number(params.stageHeight) || 0;
    const preset = getPreset(params.count);
    const outer = params.outer || { width: 5, height: 7 };
    const outerWidth = Math.max(0.01, Number(outer.width) || 5);
    const outerHeight = Math.max(0.01, Number(outer.height) || 7);
    const outerAspect = outerHeight / outerWidth;
    const isFramed = Boolean(params.isFramed);
    const frameProfileIn = Math.max(0, Number(params.frameProfileIn) || 0);
    const baseSlotWidthPct = Math.max(1, Number(params.baseSlotWidthPct) || 23.5);
    const wallGapIn = Math.max(0, Number(params.wallGapIn) || 4.7);
    const baseDisplayWidthIn = Math.max(0.01, Number(params.baseDisplayWidthIn) || 24);
    const maxGroupWidthPct = clamp(Number(params.maxGroupWidthPct) || 88, 20, 100);
    const maxGroupHeightPct = clamp(Number(params.maxGroupHeightPct) || 88, 20, 100);
    const layoutScale = clamp(Number(params.scale) || 1, 0.35, 1);
    const safeTopPx = Math.max(0, Number(preset.safeTopPx) || 0);
    const safeBottomPx = Math.max(0, Number(preset.safeBottomPx) || 0);

    if (!stageWidth || !stageHeight) {
      return { preset, positions: [], slotWidth: 0, slotHeight: 0, frameSize: 0, gap: 0 };
    }

    const frameRatio = isFramed ? frameProfileIn / outerWidth : 0;
    const gapRatio = wallGapIn / baseDisplayWidthIn;
    const preferredSlotWidthPct = Math.max(1, Number(preset.preferredSlotWidthPct) || baseSlotWidthPct);
    let slotWidth = stageWidth * (preferredSlotWidthPct / 100);
    let gap = slotWidth * gapRatio;

    if (preset.preserveLegacyThree) {
      const legacyGapPct = baseSlotWidthPct * gapRatio;
      let slotPct = baseSlotWidthPct;
      let frameSize = isFramed
        ? clamp(slotWidth * frameRatio, 1.25, slotWidth * 0.22)
        : 0;
      let framePct = (frameSize / stageWidth) * 100;
      let outerItemPct = slotPct + framePct * 2;
      let groupPct = outerItemPct * 3 + legacyGapPct * 2;

      if (groupPct > maxGroupWidthPct) {
        slotPct = (maxGroupWidthPct - legacyGapPct * 2) / (3 * (1 + frameRatio * 2));
        slotWidth = stageWidth * (slotPct / 100);
      }

      gap = stageWidth * (legacyGapPct / 100);
    } else {
      const maxGroupWidth = stageWidth * (maxGroupWidthPct / 100);
      const contentHeight = Math.max(1, stageHeight - safeTopPx - safeBottomPx);
      const maxGroupHeight = Math.min(stageHeight * (maxGroupHeightPct / 100), contentHeight);

      for (let pass = 0; pass < 4; pass += 1) {
        const frameSize = isFramed
          ? clamp(slotWidth * frameRatio, 1.25, slotWidth * 0.22)
          : 0;
        const slotHeight = slotWidth * outerAspect;
        gap = slotWidth * gapRatio;
        const groupWidth = preset.columns * (slotWidth + frameSize * 2) + (preset.columns - 1) * gap;
        const groupHeight = preset.rows * (slotHeight + frameSize * 2) + (preset.rows - 1) * gap;
        const fitScale = Math.min(1, maxGroupWidth / groupWidth, maxGroupHeight / groupHeight);
        if (fitScale >= 0.9995) break;
        slotWidth *= fitScale;
      }
    }

    slotWidth *= layoutScale;

    const frameSize = isFramed
      ? clamp(slotWidth * frameRatio, 1.25, slotWidth * 0.22)
      : 0;
    const slotHeight = slotWidth * outerAspect;
    gap = preset.preserveLegacyThree ? gap : slotWidth * gapRatio;

    const outerItemWidth = slotWidth + frameSize * 2;
    const outerItemHeight = slotHeight + frameSize * 2;
    const groupWidth = preset.columns * outerItemWidth + (preset.columns - 1) * gap;
    const groupHeight = preset.rows * outerItemHeight + (preset.rows - 1) * gap;
    const startX = (stageWidth - groupWidth) / 2;
    const contentHeight = Math.max(1, stageHeight - safeTopPx - safeBottomPx);
    const startY = preset.preserveLegacyThree
      ? (stageHeight - groupHeight) / 2
      : safeTopPx + (contentHeight - groupHeight) / 2;
    const positions = [];

    for (let index = 0; index < preset.count; index += 1) {
      const row = Math.floor(index / preset.columns);
      const column = index % preset.columns;
      positions.push({
        left: startX + column * (outerItemWidth + gap) + frameSize,
        top: startY + row * (outerItemHeight + gap) + frameSize,
        width: slotWidth,
        height: slotHeight,
        row,
        column
      });
    }

    return {
      preset,
      positions,
      slotWidth,
      slotHeight,
      frameSize,
      gap,
      groupWidth,
      groupHeight
    };
  }

  window.PSBLayoutModule = Object.freeze({
    presets: PRESETS,
    supportedCounts: SUPPORTED_COUNTS,
    getPreset,
    isSupportedCount,
    getStageAspectCss,
    getSlotColor,
    calculateLayout
  });

  window.dispatchEvent(new Event('psb:layout-presets-ready'));
})();
